import { Helmet } from "react-helmet-async";
import { useCallback, useEffect, useState } from "react";
import {
  Card,
  Table,
  Stack,
  Button,
  TableBody,
  Container,
  IconButton,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  LinearProgress,
} from "@mui/material";

import { useSnackbar } from "notistack";
import { format, isValid } from "date-fns";
// routes
import { PATH_DASHBOARD } from "../../../routes/paths";
// @types
import { IRecall } from "../../../@types/recalls";
// components
import Iconify from "../../../components/iconify";
import Scrollbar from "../../../components/scrollbar";
import CustomBreadcrumbs from "../../../components/custom-breadcrumbs";
import { useSettingsContext } from "../../../components/settings";
import CustomCampaignsDialog from "../../../components/custom-campaigns-dialog";
import SendRecallsDialog from "../../../components/send-recalls-dialog";
import {
  useTable,
  emptyRows,
  TableNoData,
  TableEmptyRows,
  TablePaginationCustom,
} from "../../../components/table";
// sections
import {
  RecallTableRow,
  RecallTableToolbar,
  RecallAnalytics,
} from "../../../sections/@dashboard/recalls";
import { getApiErrorMessage } from "../../../utils/util";

import EditRecallDialog from "../../../sections/@dashboard/recalls/EditRecallDialog";
import { RecallsService } from "../../../api/recalls.service";
import { RoleBasedSection } from "../../../components/rbac";
import { RoleGroup } from "../../../auth/types/roles";

const RECALL_CREATE_ROLES = [
  ...RoleGroup.BOOKING_STAFF,
  ...RoleGroup.MANAGEMENT_STAFF,
];
const RECALL_ADMIN_ROLES = [...RoleGroup.MANAGEMENT_STAFF];

const TABLE_HEAD = [
  { id: "patientName", label: "Patient Name", align: "left" },
  { id: "communicationType", label: "Communication Type", align: "center" },
  { id: "recallType", label: "Recall Type", align: "center" },
  { id: "stage", label: "Stage", align: "center" },
  { id: "status", label: "Status", align: "center" },
  { id: "typeOfAppointment", label: "Type of Appointment", align: "center" },
  { id: "lastExamDate", label: "Last Exam Date", align: "center" },
  { id: "recallDate", label: "Recall Date", align: "center" },
  { id: "actions", label: "Actions", align: "center" },
];

function mapRecallFromApi(recall: any): IRecall {
  return {
    id: String(recall.id),
    patientId: recall.patient?.id ? String(recall.patient.id) : undefined,
    communicationType: recall.communicationType as IRecall["communicationType"],
    recallType: recall.recallType,
    stage: recall.stage,
    typeOfAppointment: recall.recallType,
    recallDate: recall.scheduledDate,
    lastExamDate: recall.patient?.lastVisit,
    patientName:
      `${recall.patient?.firstName || ""} ${recall.patient?.surName || ""}`.trim(),
    status: recall.status,
    scheduledDate: recall.scheduledDate,
    message: recall.message,
  };
}

// ----------------------------------------------------------------------

export default function RecallsPage() {
  const { themeStretch } = useSettingsContext();
  const { enqueueSnackbar } = useSnackbar();

  const {
    dense,
    page,
    rowsPerPage,
    setPage,
    onChangePage,
    onChangeRowsPerPage,
    onChangeDense,
  } = useTable({ defaultOrderBy: "recallDate", defaultRowsPerPage: 10 });

  const [recalls, setRecalls] = useState<IRecall[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [listLoading, setListLoading] = useState(false);

  const [filterName, setFilterName] = useState("");
  const [debouncedFilterName, setDebouncedFilterName] = useState("");
  const [customCampaignsOpen, setCustomCampaignsOpen] = useState(false);
  const [sendRecallsOpen, setSendRecallsOpen] = useState(false);
  const [editRecallOpen, setEditRecallOpen] = useState(false);
  const [selectedRecall, setSelectedRecall] = useState<IRecall | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [filterStage, setFilterStage] = useState("all");
  const [sendAllLoading, setSendAllLoading] = useState(false);
  const [sendingRowId, setSendingRowId] = useState<string | null>(null);
  const denseHeight = dense ? 56 : 76;

  const isFiltered =
    filterName.trim() !== "" || selectedDate !== null || filterStage !== "all";

  const isNotFound = !listLoading && totalCount === 0;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilterName(filterName), 400);
    return () => clearTimeout(t);
  }, [filterName]);

  const loadRecalls = useCallback(async () => {
    setListLoading(true);
    try {
      const scheduledTo =
        selectedDate && isValid(selectedDate)
          ? format(selectedDate, "yyyy-MM-dd")
          : undefined;

      const recallsService = new RecallsService();
      const response = await recallsService.getRecalls({
        page,
        limit: rowsPerPage,
        patientName: debouncedFilterName.trim() || undefined,
        stage: filterStage === "all" ? undefined : Number(filterStage),
        scheduledTo,
      });

      const { data, total, limit } = response.data;
      const maxPage = Math.max(0, Math.ceil(total / limit) - 1);
      if (page > maxPage) {
        setPage(maxPage);
        return;
      }

      setRecalls((data as any[]).map(mapRecallFromApi));
      setTotalCount(total);
    } catch (error) {
      console.error("Failed to fetch recalls", error);
    } finally {
      setListLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    debouncedFilterName,
    filterStage,
    selectedDate,
    setPage,
  ]);

  useEffect(() => {
    loadRecalls();
  }, [loadRecalls]);

  const fetchRowsForExport = useCallback(async () => {
    const scheduledTo =
      selectedDate && isValid(selectedDate)
        ? format(selectedDate, "yyyy-MM-dd")
        : undefined;

    const recallsService = new RecallsService();
    const response = await recallsService.getRecalls({
      page: 0,
      limit: 5000,
      patientName: debouncedFilterName.trim() || undefined,
      stage: filterStage === "all" ? undefined : Number(filterStage),
      scheduledTo,
    });

    return (response.data.data as any[]).map(mapRecallFromApi);
  }, [debouncedFilterName, filterStage, selectedDate]);

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const handleFilterStage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setFilterStage(event.target.value);
  };

  const handleFilterStageTab = (
    event: React.SyntheticEvent,
    newValue: string,
  ) => {
    setPage(0);
    setFilterStage(newValue);
  };

  const handleDeleteRow = async (id: string) => {
    try {
      const recallsService = new RecallsService();
      await recallsService.deleteRecall(Number(id));
      await loadRecalls();
    } catch (error) {
      console.error("Failed to delete recall", error);
    }
  };

  const handleSendRow = async (id: string) => {
    try {
      setSendingRowId(id);
      const recallsService = new RecallsService();
      await recallsService.sendRecall(Number(id));
      enqueueSnackbar("Recall sent successfully!");
      await loadRecalls();
    } catch (error: any) {
      console.error("Failed to send recall", error);
      enqueueSnackbar(getApiErrorMessage(error, "Failed to send recall."), {
        variant: "error",
      });
    } finally {
      setSendingRowId(null);
    }
  };

  const handleResetFilter = () => {
    setPage(0);
    setFilterName("");
    setDebouncedFilterName("");
    setFilterStage("all");
    setSelectedDate(null);
  };

  const handleEditRecall = (recall: IRecall) => {
    setSelectedRecall(recall);
    setEditRecallOpen(true);
  };

  const handleEditRecallClose = () => {
    setEditRecallOpen(false);
    setSelectedRecall(null);
  };

  const handleEditRecallSave = async (updatedRecall: Partial<IRecall>) => {
    try {
      const recallsService = new RecallsService();

      const effectiveCommunicationType =
        updatedRecall.communicationType || selectedRecall!.communicationType;

      const updatedStage =
        effectiveCommunicationType === "SMS"
          ? 1
          : effectiveCommunicationType === "Email"
            ? 2
            : 3;

      const scheduledDate = updatedRecall.scheduledDate
        ? new Date(updatedRecall.scheduledDate as string | Date).toISOString()
        : undefined;

      await recallsService.updateRecall(selectedRecall!.id, {
        recallType: updatedRecall.recallType,
        communicationType: effectiveCommunicationType,
        message: updatedRecall.message,
        scheduledDate,
        stage: updatedStage,
      });
      enqueueSnackbar("Recall updated successfully!");
      handleEditRecallClose();
      await loadRecalls();
    } catch (error: any) {
      console.error("Failed to update recall", error);
      enqueueSnackbar(getApiErrorMessage(error, "Failed to update recall"), {
        variant: "error",
      });
    }
  };

  const handleCustomCampaignsOpen = () => {
    setCustomCampaignsOpen(true);
  };

  const handleCustomCampaignsClose = () => {
    setCustomCampaignsOpen(false);
  };

  const handleSendRecallsOpen = () => {
    setSendRecallsOpen(true);
  };

  const handleSendRecallsClose = () => {
    setSendRecallsOpen(false);
  };

  const handleDateChange = (newDate: Date | null) => {
    setPage(0);
    setSelectedDate(newDate);
  };

  const handleSendAllDue = async () => {
    try {
      setSendAllLoading(true);
      const recallsService = new RecallsService();
      let communicationType: string | undefined;

      if (filterStage === "1") communicationType = "SMS";
      if (filterStage === "2") communicationType = "Email";
      if (filterStage === "3") communicationType = "Phone Call";

      const res = await recallsService.sendAllRecalls(communicationType);

      const { totalFound, processed, skipped } = res.data;

      if (!totalFound) {
        enqueueSnackbar(
          "No scheduled recalls available to send (future dated or already sent).",
          { variant: "info" },
        );
        return;
      }

      if (processed === 0) {
        enqueueSnackbar("Recalls found but none were eligible to send.", {
          variant: "warning",
        });
        return;
      }

      enqueueSnackbar(
        `${processed} recall(s) sent successfully. ${
          skipped ? `${skipped} skipped.` : ""
        }`,
        { variant: "success" },
      );

      await loadRecalls();
    } catch (error: any) {
      enqueueSnackbar(getApiErrorMessage(error, "Failed to send recalls."), {
        variant: "error",
      });
    } finally {
      setSendAllLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Recalls | OptoPro</title>
      </Helmet>

      <Container maxWidth={themeStretch ? false : "xl"}>
        <CustomBreadcrumbs
          heading="Recalls"
          links={[
            {
              name: "Dashboard",
              href: PATH_DASHBOARD.root,
            },
            {
              name: "Recalls",
            },
          ]}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              {/* Custom Campaigns — manager/admin only */}
              <RoleBasedSection allowedRoles={RECALL_ADMIN_ROLES}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleCustomCampaignsOpen}>
                  Custom Campaigns
                </Button>
              </RoleBasedSection>

              {/* Send Recall — booking + management */}
              <RoleBasedSection allowedRoles={RECALL_CREATE_ROLES}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleSendRecallsOpen}>
                  Send Recall
                </Button>
              </RoleBasedSection>

              {/* Analytics & Send All — manager/admin only */}
              <RoleBasedSection allowedRoles={RECALL_ADMIN_ROLES}>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    startIcon={<Iconify icon="eva:pie-chart-2-fill" />}
                    onClick={() => setShowAnalytics(!showAnalytics)}>
                    {showAnalytics ? "Show List" : "Analytics"}
                  </Button>

                  <Button
                    variant="contained"
                    disabled={sendAllLoading}
                    startIcon={
                      sendAllLoading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : null
                    }
                    onClick={handleSendAllDue}>
                    {sendAllLoading ? "Sending..." : "Send All"}
                  </Button>
                </Stack>
              </RoleBasedSection>

              <IconButton onClick={() => loadRecalls()}>
                <Iconify icon="eva:refresh-fill" />
              </IconButton>
            </Stack>
          }
        />

        {showAnalytics ? (
          <RecallAnalytics />
        ) : (
          <Card sx={{ position: "relative" }}>
            {listLoading ? (
              <LinearProgress
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 2,
                }}
              />
            ) : null}
            <Tabs
              value={filterStage}
              onChange={handleFilterStageTab}
              sx={{
                px: 2,
                bgcolor: "background.neutral",
              }}>
              {[
                { value: "all", label: "All" },
                { value: "1", label: "Stage 1 (SMS)" },
                { value: "2", label: "Stage 2 (Email)" },
                { value: "3", label: "Stage 3 (Call List)" },
              ].map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={tab.label}
                  sx={{ textTransform: "capitalize" }}
                />
              ))}
            </Tabs>

            <Divider />

            <RecallTableToolbar
              isFiltered={isFiltered}
              filterName={filterName}
              selectedDate={selectedDate}
              filterStage={filterStage}
              recalls={recalls}
              onFilterName={handleFilterName}
              onFilterStage={handleFilterStage}
              onDateChange={handleDateChange}
              onResetFilter={handleResetFilter}
              fetchRowsForExport={fetchRowsForExport}
            />

            <TableContainer sx={{ position: "relative", overflow: "unset" }}>
              <Scrollbar>
                <Table size={dense ? "small" : "medium"} sx={{ minWidth: 800 }}>
                  <TableHead>
                    <TableRow>
                      {TABLE_HEAD.map((headCell) => (
                        <TableCell
                          key={headCell.id}
                          align={
                            (headCell.align || "center") as
                              | "center"
                              | "right"
                              | "left"
                              | "inherit"
                              | "justify"
                          }>
                          {headCell.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {recalls.map((row) => (
                      <RecallTableRow
                        key={row.id}
                        row={row}
                        loading={sendingRowId === row.id}
                        onDeleteRow={() => handleDeleteRow(row.id)}
                        onSendRow={() => handleSendRow(row.id)}
                        onEditRow={() => handleEditRecall(row)}
                      />
                    ))}

                    <TableEmptyRows
                      height={denseHeight}
                      emptyRows={emptyRows(page, rowsPerPage, recalls.length)}
                    />

                    <TableNoData isNotFound={isNotFound} />
                  </TableBody>
                </Table>
              </Scrollbar>
            </TableContainer>

            <TablePaginationCustom
              count={totalCount}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
              dense={dense}
              onChangeDense={onChangeDense}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Card>
        )}
      </Container>

      <CustomCampaignsDialog
        open={customCampaignsOpen}
        onClose={handleCustomCampaignsClose}
      />

      <SendRecallsDialog
        open={sendRecallsOpen}
        onClose={handleSendRecallsClose}
        onRecallCreated={() => loadRecalls()}
      />

      <EditRecallDialog
        open={editRecallOpen}
        recall={selectedRecall}
        onClose={handleEditRecallClose}
        onSave={handleEditRecallSave}
      />
    </>
  );
}
